import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import useConfig from '@/features/configs/useConfig'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { ApprovalInvitationStatus, ApprovalStatus, ContributorStatus, OfferStatus } from '@/features/offers/constants'
import useOfferList from '@/features/offers/hooks/useOfferList'
import { authSelector } from '@/stores/auth/authSlice'
import { Flex, Text, VStack } from '@chakra-ui/react'
import BaseOfferTaskWidget from './BaseOfferTaskWidget'
import OfferTaskButton from './OfferTaskButton'

const OfferApprovalTaskWidget = () => {
  const { user } = useSelector(authSelector)

  const { useFetchOfferApprovals } = useOfferList()
  const { useFetchConfig } = useConfig()
  const { data: offerApprovalResponse } = useFetchOfferApprovals({
    page: 1,
    pageSize: 0,
    filters: {
      approvalStatus: [
        ApprovalStatus.APPROVED,
        ApprovalStatus.UPCOMING,
        ApprovalStatus.REJECTED,
        ContributorStatus.REVIEWED,
        ApprovalInvitationStatus.REPLIED,
        ContributorStatus.COMMENTED,
        ApprovalInvitationStatus.INVITED,
        ApprovalStatus.REQUIRED,
      ],
    },
  })

  //My Approval
  const processedApprovalStatus = [
    ApprovalStatus.APPROVED,
    ApprovalStatus.REJECTED,
    ContributorStatus.REVIEWED,
    ApprovalInvitationStatus.REPLIED,
  ]

  const actionRequiredApprovalStatus = [
    ContributorStatus.COMMENTED,

    ApprovalInvitationStatus.INVITED,
    ApprovalStatus.REQUIRED,
  ]

  const offerStatusForApproval = [
    ContributorGroupType.QS,
    ContributorGroupType.LEGAL,
    ContributorGroupType.EXPORT_CONTROL,
  ].includes(user?.contributorGroup)
    ? [
        OfferStatus.QUOTATION_DRAFT,
        OfferStatus.QUOTATION_REVIEWED,
        OfferStatus.QUOTATION_SUBMITTED,
        OfferStatus.BOOKING_SUBMITTED,
      ]
    : [OfferStatus.QUOTATION_REVIEWED, OfferStatus.QUOTATION_SUBMITTED, OfferStatus.BOOKING_SUBMITTED]

  const myApprovalActionRequiredLink = `/offers/approvals?approvalStatus=${actionRequiredApprovalStatus.join('%2C')}&status=${offerStatusForApproval.join('%2C')}`

  const upcomingApprovalOffers = offerApprovalResponse?.data
    ? offerApprovalResponse.data.filter((offer) => offer.approvalStatus === ApprovalStatus.UPCOMING)
    : []

  const requiredApprovalOffers = offerApprovalResponse?.data
    ? offerApprovalResponse.data.filter((offer) => {
        return (
          actionRequiredApprovalStatus.includes(offer.approvalStatus) && offerStatusForApproval.includes(offer.status)
        )
      })
    : []
  const processedApprovalOffers = offerApprovalResponse?.data
    ? offerApprovalResponse.data.filter((offer) => processedApprovalStatus.includes(offer.approvalStatus))
    : []

  const canViewMyApproval = useMemo(() => {
    return offerApprovalResponse?.data && offerApprovalResponse.data.length > 0
  }, [offerApprovalResponse])

  if (!canViewMyApproval) {
    return <></>
  }

  return (
    <BaseOfferTaskWidget title={'My Offer/Booking Approval'}>
      <Flex gap="5" direction={['column', 'row']} wrap="wrap">
        <OfferTaskButton
          color="teal"
          buttonText={'Upcoming'}
          count={upcomingApprovalOffers.length}
          link={'/offers/approvals?approvalStatus=Upcoming'}
        />
        <OfferTaskButton
          color="teal"
          buttonText={'Action Required'}
          count={requiredApprovalOffers.length}
          link={myApprovalActionRequiredLink}
        />
      </Flex>
      <Flex>
        <OfferTaskButton
          color="teal"
          buttonText={'Processed'}
          count={processedApprovalOffers.length}
          link={`/offers/approvals?approvalStatus=${processedApprovalStatus.join('%2C')}`}
          label={
            <VStack>
              {processedApprovalStatus.map((status) => {
                return (
                  <Text key={status}>
                    {status}:{processedApprovalOffers.filter((offer) => offer.approvalStatus === status).length}
                  </Text>
                )
              })}
            </VStack>
          }
        />
      </Flex>
    </BaseOfferTaskWidget>
  )
}

export default OfferApprovalTaskWidget
